import { Box, Typography } from "@mui/material";
import { type FC, type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemRoundedCheckbox } from "../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import type { PbjStaffingBulkEdits, PbjStaffingHoursRow } from "../../../utils/models";
import { useBulkEditPbjStaffingShifts } from "../hooks/usePbjAudit";
import { usePayTypeCodeOptions } from "./staffingEditCells";

type FieldKey = keyof PbjStaffingBulkEdits;

type Props = {
	open: boolean;
	onClose: () => void;
	/** The selected grid rows; each is sent as its group key so the server resolves ALL contributing shifts. */
	selectedRows: PbjStaffingHoursRow[];
	/** PBJ job picker options, including the leading clear ("—") entry (same list the inline editor uses). */
	jobOptions: SystemAutoCompleteOption<string>[];
	/** Empeon-only depts never populate EarnCode — hide that field to match the grid. */
	hideSourceSpecificColumns: boolean;
	/** Called after a successful apply (exit selection mode / reset selection). */
	onApplied: () => void;
};

const INITIAL_ENABLED: Record<FieldKey, boolean> = {
	pbjJobId: false,
	rawJob: false,
	departmentName: false,
	earnCode: false,
	agencyName: false,
	payTypeCode: false,
};
const INITIAL_VALUES: Record<FieldKey, string> = {
	pbjJobId: "",
	rawJob: "",
	departmentName: "",
	earnCode: "",
	agencyName: "",
	payTypeCode: "",
};
const CHECKBOX_COLUMN_WIDTH = 190;

/**
 * Generic bulk editor for the selected Staffing-Hours rows: any subset of the 6 editable fields can
 * be applied in one PATCH. A field is only sent when its checkbox is checked (absent = untouched);
 * a checked field left empty (or set to the "—" option) sends an explicit null = clear (for Pay
 * Type Code, clear reverts to the derived value).
 */
export const BulkEditStaffingDialog: FC<Props> = ({ open, onClose, selectedRows, jobOptions, hideSourceSpecificColumns, onApplied }) => {
	const { t } = useTranslation();
	const payTypeOptions = usePayTypeCodeOptions();
	const bulkMutation = useBulkEditPbjStaffingShifts();

	const [enabled, setEnabled] = useState<Record<FieldKey, boolean>>(INITIAL_ENABLED);
	const [values, setValues] = useState<Record<FieldKey, string>>(INITIAL_VALUES);

	// Fresh form every time the dialog opens — a previous bulk edit must never leak into a new one.
	useEffect(() => {
		if (open) {
			setEnabled(INITIAL_ENABLED);
			setValues(INITIAL_VALUES);
		}
	}, [open]);

	const anyEnabled = useMemo(() => Object.values(enabled).some(Boolean), [enabled]);

	const handleConfirm = async () => {
		const edits: PbjStaffingBulkEdits = {};
		for (const key of Object.keys(enabled) as FieldKey[]) {
			if (!enabled[key]) continue;
			const trimmed = values[key].trim();
			edits[key] = trimmed === "" ? null : trimmed;
		}
		try {
			await bulkMutation.mutateAsync({
				rows: selectedRows.map((r) => ({ pbjEmployeeId: r.empId, date: r.date, pbjJobId: r.pbjJobId })),
				edits,
			});
			onClose();
			onApplied();
		} catch {
			// Keep the dialog open so the user can retry; the mutation's onError already showed the snackbar.
		}
	};

	const fieldRow = (key: FieldKey, label: string, control: ReactNode) => (
		<Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
			<Box sx={{ width: CHECKBOX_COLUMN_WIDTH, flexShrink: 0 }}>
				<SystemRoundedCheckbox
					checked={enabled[key]}
					onChange={(checked) => setEnabled((prev) => ({ ...prev, [key]: checked }))}
					label={label}
					data-testid={`pbj-staffing-bulk-field-${key}-toggle`}
				/>
			</Box>
			<Box sx={{ flex: 1, minWidth: 0, opacity: enabled[key] ? 1 : 0.5 }}>{control}</Box>
		</Box>
	);

	const selectControl = (key: FieldKey, options: SystemAutoCompleteOption<string>[]) => (
		<SystemAutoComplete<string>
			fullWidth
			options={options}
			value={options.find((o) => o.value === values[key]) ?? null}
			onChange={(_e, opt) => setValues((prev) => ({ ...prev, [key]: opt?.value ?? "" }))}
			textFieldProps={{ disabled: !enabled[key], reserveHeightForHelperText: false }}
			dataTestId={`pbj-staffing-bulk-field-${key}-input`}
		/>
	);

	const textControl = (key: FieldKey) => (
		<SystemValidationTextField
			fullWidth
			value={values[key]}
			onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
			placeholder={enabled[key] ? t("pbj_model.staffing.bulk.empty_clears") : undefined}
			disabled={!enabled[key]}
			reserveHeightForHelperText={false}
			dataTestId={`pbj-staffing-bulk-field-${key}-input`}
		/>
	);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("pbj_model.staffing.bulk.title", { num: selectedRows.length })}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: { sm: 480 }, pt: 0.5 }}>
					<Typography variant="body2" color="text.secondary">
						{t("pbj_model.staffing.bulk.hint")}
					</Typography>
					{fieldRow("pbjJobId", t("pbj_model.staffing.col.pbj_job"), selectControl("pbjJobId", jobOptions))}
					{fieldRow("rawJob", t("pbj_model.staffing.col.job_description"), textControl("rawJob"))}
					{fieldRow("departmentName", t("pbj_model.staffing.col.department_name"), textControl("departmentName"))}
					{!hideSourceSpecificColumns && fieldRow("earnCode", t("pbj_model.staffing.col.earn_code"), textControl("earnCode"))}
					{fieldRow("agencyName", t("pbj_model.staffing.col.agency_name"), textControl("agencyName"))}
					{fieldRow("payTypeCode", t("pbj_model.staffing.col.pay_type_code"), selectControl("payTypeCode", payTypeOptions))}
				</Box>
			}
			confirm={t("pbj_model.staffing.bulk.apply")}
			onConfirm={handleConfirm}
			confirmButtonVariant="primary"
			confirmDisabled={!anyEnabled || selectedRows.length === 0}
			confirmTooltip={anyEnabled ? undefined : t("pbj_model.staffing.bulk.no_fields")}
			confirmLoading={bulkMutation.isPending}
			dataTestId="pbj-staffing-bulk-edit-dialog"
		/>
	);
};
